/**
 * Path: packages/frontend/src/store/workspace.ts
 * Task: Manages the active workspace state, including primary, secondary, and preview entity selections.
 */

import { defineStore } from 'pinia';
import type { Entity } from '@/core/types/editor';
import { useDatastoreStore } from './datastore';
import { useUiStore } from './ui';

export const useWorkspaceStore = defineStore('workspace', {
  state: () => ({
    isLocked: true,
    selectedEntityId: null as string | null,
    selectedEntity: null as Entity | null,
    originalEntity: null as Entity | null,

    secondaryIsLocked: true,
    secondarySelectedEntityId: null as string | null,
    secondarySelectedEntity: null as Entity | null,
    secondaryOriginalEntity: null as Entity | null,

    isNewEntityDialogOpen: false,
    draftEntity: null as Entity | null,

    previewEntityId: null as string | null,
    previewEntity: null as Entity | null,
    isPreviewModalOpen: false
  }),

  actions: {
    setSelectedEntity(entityId: string | null) {
      this.selectedEntityId = entityId;
      if (!entityId) {
        this.selectedEntity = null;
        this.originalEntity = null;
        return;
      }
      const datastore = useDatastoreStore();
      const entity = datastore.allEntities.find((e: Entity) => e.id === entityId);
      if (entity) {
        this.selectedEntity = JSON.parse(JSON.stringify(entity));
        this.originalEntity = JSON.parse(JSON.stringify(entity));
        this.isLocked = true;
      }
    },

    setSecondarySelectedEntity(entityId: string | null) {
      this.secondarySelectedEntityId = entityId;
      if (!entityId) {
        this.secondarySelectedEntity = null;
        this.secondaryOriginalEntity = null;
        return;
      }
      const datastore = useDatastoreStore();
      const entity = datastore.allEntities.find((e: Entity) => e.id === entityId);
      if (entity) {
        this.secondarySelectedEntity = JSON.parse(JSON.stringify(entity));
        this.secondaryOriginalEntity = JSON.parse(JSON.stringify(entity));
        this.secondaryIsLocked = true;
      }
    },

    closeSecondarySelectedEntity() {
      const uiStore = useUiStore();
      this.secondarySelectedEntityId = null;
      this.secondarySelectedEntity = null;
      this.secondaryOriginalEntity = null;
      uiStore.panels.showSecondaryDetails = false;
    },

    /* Capability to spawn a tertiary preview modal without losing the secondary pane */
    setPreviewEntity(entityId: string | null) {
      // By strictly using the preview state without hijacking the secondary state,
      // we ensure that links clicked inside the right-pane strictly open in a modal,
      // and links clicked within the modal replace the modal content dynamically.
      // This totally isolates map highlighting from exploratory clicks.
      this.previewEntityId = entityId;

      if (!entityId) {
        this.previewEntity = null;
        this.isPreviewModalOpen = false;
        return;
      }

      const datastore = useDatastoreStore();
      const entity = datastore.allEntities.find((e: Entity) => e.id === entityId);

      if (entity) {
        this.previewEntity = JSON.parse(JSON.stringify(entity));
        this.isPreviewModalOpen = true;
      }
    },

    closePreviewEntity() {
      this.previewEntityId = null;
      this.previewEntity = null;
      this.isPreviewModalOpen = false;
    },

    resetSelectedEntity(isSecondary = false, isDraft = false) {
      if (isDraft) {
        this.isNewEntityDialogOpen = false;
        this.draftEntity = null;
        return;
      }
      const dataStore = useDatastoreStore();
      if (isSecondary) {
        if (this.secondaryOriginalEntity) {
          const list = dataStore.data?.[this.secondaryOriginalEntity.entityType] as Entity[] | undefined;
          const exists = list?.some(e => e.id === this.secondaryOriginalEntity?.id);
          if (!exists) this.closeSecondarySelectedEntity();
          else this.secondarySelectedEntity = JSON.parse(JSON.stringify(this.secondaryOriginalEntity));
        }
      } else {
        if (this.originalEntity) {
          const list = dataStore.data?.[this.originalEntity.entityType] as Entity[] | undefined;
          const exists = list?.some(e => e.id === this.originalEntity?.id);
          if (!exists) this.setSelectedEntity(null);
          else this.selectedEntity = JSON.parse(JSON.stringify(this.originalEntity));
        }
      }
    }
  }
});
